<template>
  <div class="home-carousel-component">
    <div :id="carouselIdentifier" class="carousel" :class="{ 'carousel-vertical': vertical }">
      <div class="carousel-wrapper">
        <div
          class="carousel-inner"
          :style="{
            transform: `translate${vertical ? 'Y' : 'X'}(${currentOffset}px)`,
            transition: transitionStyle,
            flexBasis:
              slides.slide < 2 && isnothomepage ? calculateForOneImage() : `${slideWidth}px`,
            msFlexPreferredSize: `${slideWidth}px`,
            visibility: `${slideWidth ? 'visible' : 'hidden'}`,
          }"
        >
          <div
            v-for="(slide, slideIndex) in slides.carouselslide"
            :key="slideIndex"
            class="carousel-slide"
            @click="redirectPage(slide.CarouselCTA)"
          >
            <div
              v-if="slide.CarouselImage && slide.CarouselImage.Src"
              class="carousel-slide__background-image lazyload"
              :data-bg="slide.CarouselImage.Src"
            ></div>
            <div class="carousel-silde-container">
              <div class="carousel-slider-inner-content">
                <h2 class="carousel-slide_title" :class="{ 'white-text': !slide.FontColor }">
                  {{ slide.CarouselTitle }}
                </h2>
                <span class="carousel-slide_desc" :class="{ 'white-text': !slide.FontColor }">{{
                  slide.CarouselDescription
                }}</span>
                <a
                  :href="slide.CarouselCTA"
                  class="carousel-slide_cta ripple-animation ripple-animation--black"
                  @click="sliderBtnClick(slide.CarouselCTA, slide.CarouselTitle)"
                  >{{ slide.CarouselCTAText }}</a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="paginationEnabled && pageCount > 1" class="carousel-pagination">
        <ul class="carousel-dot-container">
          <li
            v-for="(page, index) in pageCount"
            :key="index"
            class="carousel-dot"
            :class="{ 'carousel-dot--active': index === currentPage }"
            @click="goToPage(index, { action: 'display' })"
          >
            <button
              style="outline: none; width: 18px; height: 18px"
              class="carousel-dot-inner"
            ></button>
          </li>
        </ul>
      </div>
      <navigation
        v-if="navigationEnabled"
        :prev-disabled="isPrevDisabled"
        :next-disabled="isNextDisabled"
        @page-advance-click="advancePage"
      >
      </navigation>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import debounce from 'lodash/debounce';
import navigation from './homepage-navigation.vue';

const MILLISECONDS_PER_SECOND = 1000;

export default {
  name: 'homepagecarousel',
  components: {
    [navigation.name]: navigation,
  },
  props: {
    // to differntiate main page carousel from other carousel
    isnothomepage: { type: Boolean, default: false },
    slides: {
      type: Object,
      required: true,
    },
    // slide transition easing. any valid CSS transition easing accepted
    easing: { type: String, default: 'ease' },

    // Minimum distance for the swipe to trigger a slide advance
    minSwipeDistance: { type: Number, default: 50 },

    // Flag to render the slide component (next/prev buttons)
    navigationEnabled: { type: Boolean, default: true },

    // Flag to render pagination component
    paginationEnabled: { type: Boolean, default: true },

    // Maximum number of slides displayed on each page
    perPage: { type: Number, default: 1 },

    // Vertical instead of horizontal
    vertical: { type: Boolean, default: false },

    // the offset between items
    slideSpacing: { type: Object, default: () => ({}) },

    // padding for the slides
    slidePadding: { type: Number, default: 0 },

    /**
     * Configure the number of visible slides with a particular browser width.
     * This will be an array of arrays, ex. [[320, 2], [1199, 4]]
     * Formatted as [x, y] where x=browser width, and y=number of slides displayed.
     * ex. [1199, 4] means if (window <= 1199) then show 4 slides per page
     */
    perPageCustom: { type: Array },

    // Scroll per page, not per item
    scrollPerPage: { type: Boolean, default: true },

    // slide transition speed. Number of milliseconds accepted
    speed: { type: Number, default: 400 },

    // Start at slide
    startAt: { type: Number, default: 0 },

    // Start at slide
    carouselIdentifier: {
      type: String,
      default: () => `carousel-${Date.now()}`,
    },

    // If the carousel should automatically rotate to next slide.
    autoRotate: { type: Boolean, default: true },

    // # of seconds before rotating to the next slide.
    rotationTiming: { type: Number, default: 5 },

    // Infinite loop
    infinite: { type: Boolean, default: true },

    // Tag to fire when carousel rotates to a new slide
    rotateTag: { type: Object, required: true },
  },
  data() {
    return {
      browserWidth: null,
      carouselSize: null,
      currentPage: this.startAt,
      prevPage: this.startAt,
      dragOffset: 0,
      dragStartX: 0,
      mousedown: false,
      slideCount: 0,
      maxSwipeAngle: 40,
      isAutoRotating: this.autoRotate,
      scrolling: false,
    };
  },
  computed: {
    /**
     * Given a viewport width, find the number of slides to display
     * @return {Number}       Number of slides to display
     */
    breakpointSlidesPerPage() {
      if (!this.perPageCustom) {
        return this.perPage;
      }

      const breakpointArray = this.perPageCustom;
      const width = this.browserWidth;

      const breakpoints = breakpointArray.sort((a, b) => (a[0] > b[0] ? -1 : 1));

      // Reduce the breakpoints to entries where the width is in range
      // The breakpoint arrays are formatted as [widthToMatch, numberOfSlides]
      const matches = breakpoints.filter((breakpoint) => width >= breakpoint[0]);

      // If there is a match, the result should return only
      // the slide count from the first matching breakpoint
      const match = matches[0] && matches[0][1];

      return match || this.perPage;
    },

    /**
     * @return {Boolean} Can the slider move forward?
     */
    canAdvanceForward() {
      return this.currentPage < this.pageCount - 1;
    },

    /**
     * @return {Boolean} Can the slider move backward?
     */
    canAdvanceBackward() {
      return this.currentPage > 0;
    },

    /**
     * Number of slides to display per page in the current context.
     * This is constant unless responsive perPage option is set.
     * @return {Number} The number of slides per page to display
     */
    currentPerPage() {
      return !this.perPageCustom || this.$isServer ? this.perPage : this.breakpointSlidesPerPage;
    },

    /**
     * The horizontal distance the inner wrapper is offset while navigating.
     * @return {Number} Pixel value of offset to apply
     */
    currentOffset() {
      const page = this.currentPage;
      const width = this.slideWidth;
      const dragged = this.dragOffset;

      // The offset distance depends on whether the scrollPerPage option is active.
      // If this option is active, the offset will be determined per page rather than per item.
      const offset = this.scrollPerPage ? page * width * this.currentPerPage : page * width;
      const currentOffset = (offset + dragged) * -1;
      const slideSpacing = this.homegetSlideSpacing();
      if (
        document.querySelector('html').attributes.lang !== undefined &&
        document.querySelector('html').attributes.lang.value === 'he'
      ) {
        return -(currentOffset + slideSpacing);
      }

      return currentOffset + slideSpacing;
    },

    isHidden() {
      return this.carouselSize <= 0;
    },

    /**
     * Calculate the number of pages of slides
     * @return {Number} Number of pages
     */
    pageCount() {
      const slideCount = this.slideCount;
      const perPage = this.currentPerPage;

      if (this.scrollPerPage) {
        const pages = Math.ceil(slideCount / perPage);
        return pages < 1 ? 1 : pages; // Be sure to not round down to zero pages
      }

      return slideCount - (this.currentPerPage - 1);
    },

    /**
     * Calculate the width of each slide
     * @return {Number} slide width
     */
    slideWidth() {
      const width = this.carouselSize;
      const perPage = this.currentPerPage;
      const slideWidth = width / perPage;

      return slideWidth - this.homegetSlideSpacing() * 2;
    },

    transitionStyle() {
      return `${this.speed / 1000}s ${this.easing} transform`;
    },

    isPrevDisabled() {
      return !this.infinite && !this.canAdvanceBackward;
    },

    isNextDisabled() {
      return !this.infinite && !this.canAdvanceForward;
    },
  },
  beforeUpdate() {
    this.computeCarouselSize();
  },

  mounted() {
    /* istanbul ignore if: this will never execute, b/c testing is "server-side" */
    if (!this.$isServer) {
      window.addEventListener('resize', debounce(this.computeCarouselSize, 16));

      let scrollTimer = null;
      window.addEventListener('scroll', () => {
        this.scrolling = true;
        window.clearInterval(scrollTimer);
        scrollTimer = setTimeout(() => {
          this.scrolling = false;
        }, 500);
      });

      if ('ontouchstart' in window) {
        this.$el.addEventListener('touchstart', this.handleMousedown);
        this.$el.addEventListener('touchend', this.handleMouseup);
        this.$el.addEventListener('touchmove', this.handleMousemove);
      } else {
        this.$el.addEventListener('mousedown', this.handleMousedown);
        this.$el.addEventListener('mouseup', this.handleMouseup);
        this.$el.addEventListener('mousemove', this.handleMousemove);
      }

      this.scheduleAutoRotate();
      this.attachMutationObserver();
    }
  },

  created() {
    this.computeCarouselSize();

    // Compute again on next tick for dynamic slides
    Vue.nextTick(() => {
      this.computeCarouselSize();
    });
  },

  beforeDestroy() {
    /* istanbul ignore if: this will never execute, b/c testing is "server-side" */
    if (!this.$isServer) {
      this.cancelRotate();
      this.homedetachMutationObserver();
      window.removeEventListener('resize', this.homegetBrowserWidth);
      if ('ontouchstart' in window) {
        this.$el.removeEventListener('touchmove', this.handleMousemove);
      } else {
        this.$el.removeEventListener('mousemove', this.handleMousemove);
      }
    }
  },
  methods: {
    /**
     * Increase/decrease the current page value
     * @param  {String} direction (Optional) The direction to advance
     * @param {boolean} auto - Whether this was triggered automatically.
     */

    redirectPage(url) {
      window.location.href = url;
    },
    calculateForOneImage() {
      const mobileWidth = `${this.slideWidth}${'px'}`;
      if (window.innerWidth < 768) {
        return mobileWidth;
      }
      return '';
    },
    advancePage(direction, { auto = false } = {}) {
      if (direction && direction === 'backward') {
        if (this.canAdvanceBackward) {
          this.goToPage(this.currentPage - 1, { auto, action: 'previous' });
        } else if (this.infinite) {
          this.goToPage(this.pageCount - 1, { auto, action: 'previous' });
        }
      } else if (!direction || direction !== 'backward') {
        if (this.canAdvanceForward) {
          this.goToPage(this.currentPage + 1, { auto, action: 'next' });
        } else if (this.infinite) {
          this.goToPage(0, { auto, action: 'next' });
        }
      }

      this.cancelAndRescheduleRotate();
    },

    /**
     * A mutation observer is used to detect changes to the containing node
     * in order to keep the magnet container in sync with the height its reference node.
     * Note: This does not work for ie10.
     */
    attachMutationObserver() {
      const MutationObserver =
        window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;

      /* istanbul ignore if: no mutation observer in jest tests */
      if (MutationObserver) {
        const config = { attributes: true, data: true };
        this.mutationObserver = new MutationObserver(() => {
          this.$nextTick(() => {
            this.computeCarouselSize();
          });
        });
        if (this.$parent.$el) {
          this.mutationObserver.observe(this.$parent.$el, config);
        }
      }
    },

    homegetSlideSpacing() {
      let value = 0;
      for (const key in this.slideSpacing) {
        if (Object.prototype.hasOwnProperty.call(this.slideSpacing, key)) {
          if (key <= this.homegetBrowserWidth()) value = this.slideSpacing[key];
        }
      }
      return value;
    },

    /**
     * Stop listening to mutation changes
     */
    homedetachMutationObserver() {
      /* istanbul ignore if: no mutation observer in jest tests */
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    },

    /**
     * Get the current browser viewport width
     * @return {Number} Browser"s width in pixels
     */
    homegetBrowserWidth() {
      this.browserWidth = window.innerWidth;
      return this.browserWidth;
    },

    /**
     * Get the width of the carousel DOM element
     * @return {Number} Width of the carousel in pixels
     */
    homegetCarouselSize() {
      if (this.vertical) {
        this.carouselSize = (this.$el && this.$el.clientHeight) || 0; // Assign globally
      } else {
        this.carouselSize = (this.$el && this.$el.clientWidth) || 0; // Assign globally
      }

      return this.carouselSize;
    },

    /**
     * Filter slot contents to slide instances and return length
     * @return {Number} The number of slides
     */
    getSlideCount() {
      this.slideCount = this.slides.slide;
    },
    sliderBtnClick(slideUrl, slideTitle) {
      let url, spliceUrl, updated_url;
      if (slideUrl != '') {
        url = slideUrl.split('/');
        spliceUrl = url.splice(1);
        updated_url = spliceUrl.join('-');
      } else {
        updated_url = 'home';
      }
      dataLayer.push({
        event: 'uaevent',
        event_name: 'slider_button_click',
        ecommerce: 'undefined',
        eventCategory: updated_url,
        eventAction: 'select::slider navigation',
        eventLabel: `${[slideTitle.toLowerCase().trim()]}::${[slideUrl.toLowerCase().trim()]}`,
        cta_name: `${[slideTitle.toLowerCase().trim()]}`,
        link_url: `${[slideUrl.toLowerCase().trim()]}`,
        module_name: slideTitle.toLowerCase().trim() || 'slider carousel',
      });
    },

    /**
     * Set the current page to a specific value
     * This function will only apply the change if the value is within the carousel bounds
     * @param  {Number} page The value of the new page number
     * @param {string} [action] - Tag
     * @param {boolean} auto - Whether this was automatically called.
     */
    goToPage(page = {}) {
      if (page >= 0 && page < this.pageCount) {
        this.currentPage = page;
        this.$emit('pageChange', this.currentPage);

        /* istanbul ignore if: no eventBus in tests */
        if (window.eventBus) {
          window.eventBus.$emit(
            `${this.carouselIdentifier}-pageChange`,
            this.currentPage,
            this.prevPage,
          );
        }

        this.prevPage = this.currentPage;
      }
    },

    /**
     * Get the tag for the current slide.
     *
     * @return {string} The tag name for the current slide.
     */

    /**
     * Trigger actions when mouse is pressed
     * @param  {Object} e The event object
     */
    handleMousedown(e) {
      if (this.scrolling) return;

      if (!e.touches) {
        e.preventDefault();
      }

      this.mousedown = true;
      this.dragStart = e.touches ? e.touches[0] : e;
    },

    /**
     * Trigger actions when mouse is released
     */
    handleMouseup() {
      this.mousedown = false;
      this.dragOffset = 0;
    },

    /**
     * We know the main Distance swiped and the sub direction distance swiped
     * based on that we can calculate the swipe angle
     * @param {number} main
     * @param {number} sub
     * @returns {number} the angle based on Tangent: tan(Î¸) = Opposite / Adjacent
     */
    swipeAngle(main, sub) {
      return Math.atan(Math.abs(sub) / Math.abs(main)) * (180 / Math.PI);
    },

    /**
     * Trigger actions when mouse is pressed and then moved (mouse drag)
     * @param  {Object} e The event object
     */

    handleMousemove(e) {
      /* istanbul ignore next: how can we test this? mock event object? */
      if (!this.mousedown || this.scrolling) return;

      const event = e.touches ? e.touches[0] : e;

      let mainDirectionDragDistance = 0;
      let subDirectionDragDistance = 0;

      if (this.vertical) {
        mainDirectionDragDistance = this.dragStart.clientY - event.clientY;
        subDirectionDragDistance = this.dragStart.clientX - event.clientX;
      } else {
        mainDirectionDragDistance = this.dragStart.clientX - event.clientX;
        subDirectionDragDistance = this.dragStart.clientY - event.clientY;
      }

      const angle = this.swipeAngle(mainDirectionDragDistance, subDirectionDragDistance);

      /**
       * If the swipe angle is less then the max swipe angle then
       * the user is probably using the slideshow to see the next slide
       *
       * We want to use prevent default to prevent the page from scrolling when switching to a new slide.
       */
      if (angle < this.maxSwipeAngle) {
        e.preventDefault();
      }

      this.dragOffset = mainDirectionDragDistance;

      if (this.dragOffset > this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage();
      } else if (this.dragOffset < -this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage('backward');
      }
    },

    /**
     * Re-compute the width of the carousel and its slides
     */
    computeCarouselSize() {
      this.getSlideCount();
      this.homegetBrowserWidth();
      this.homegetCarouselSize();
    },

    /**
     * Schedules for
     */
    scheduleAutoRotate() {
      if (!this.autoRotate) {
        return;
      }

      this.isAutoRotating = true;
      this.rotationTimeout = setTimeout(() => {
        this.advancePage('forward', { auto: true });
      }, this.rotationTiming * MILLISECONDS_PER_SECOND);
    },

    cancelRotate() {
      this.isAutoRotating = false;
      clearTimeout(this.rotationTimeout);
    },

    cancelAndRescheduleRotate() {
      this.cancelRotate();
      this.scheduleAutoRotate();
    },

    toggleAutoRotation() {
      if (this.isAutoRotating) {
        this.cancelRotate();
      } else {
        this.cancelAndRescheduleRotate();
      }
    },
  },
};
</script>
